import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
// import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage"; // Valtech Custom Code
import Badge from "@insite/mobius/Badge";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@store/Overrides/Components/ProductPrimaryImage";
import { buttonResetProps } from "@store/theme/props/button";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";

const _zoomViewSize = 95;
/* Valtech Custom Code */

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                height: auto !important;
                max-width: ${_zoomViewSize}vw !important;
                max-height: ${_zoomViewSize}vh !important;
            `,
            modalTitle: css`
                position: absolute;
                top: 0;
                right: 0;
                z-index: 1;
                padding: 0;
                border: 0;
            `,
            modalContent: css`
                padding: 0;
                text-align: center;
            `,
        },
        closeButtonProps: {
            ...buttonResetProps,
            shape: "rectangle",
            size: undefined,
            css: css`
                ${buttonResetProps.css}
                background-color: transparent;
                padding: 0.5em;
                height: auto;
                transition: none;

                &:focus {
                    outline-offset: -${({ theme }) => theme?.focus?.width};
                }
            `,
        },
    },
    fullPrimaryImage: {
        centeringWrapper: {
            css: css`
                margin: 0;
            `,
        },
        img: {
            css: css`
                display: block;
                width: auto;
                max-height: ${_zoomViewSize}vh;
                max-width: ${_zoomViewSize}vw;
            `,
        },
    },
    primaryImage: {
        centeringWrapper: {
            css: css`
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            { values: ["600px", "500px", null, "600px"], properties: ["max-width"] },
                            { values: ["auto", "500px", null, "600px"], properties: ["height"] },
                            { values: ["500px", "600px", null, null], properties: ["max-height"] },
                        ],
                        mediaOptions.min,
                    )}
                display: flex;
                align-items: center;
                justify-content: center;
            `,
        },
        img: {
            css: css`
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            `,
        },
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const { product } = productContext;
    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;
    /* Valtech Custom Code */
    const zoomProps: any = {};
    if (!path) {
        zoomProps.onClick = null;
    } else {
        zoomProps.extendedStyles = {
            ...styles.primaryImage,
            img: {
                css: css`
                    ${styles.primaryImage?.img?.css}
                    cursor: zoom-in;
                `,
            },
        };
    }
    /* Valtech Custom Code */

    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    return (
        <>
            {!showOverlayBadge && (
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    onClick={imageClickHandler}
                    extendedStyles={styles.primaryImage}
                    {...zoomProps} // Valtech Custom Code
                />
            )}
            {showOverlayBadge && overlayBadge && (
                <StyledWrapper {...styles.badgeAreaWrapper}>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                        {...zoomProps} // Valtech Custom Code
                    />
                    <StyledWrapper {...overlayBadgeWrapperStyles}>
                        <Badge
                            type={overlayBadge.badgeType}
                            value={
                                overlayBadge.badgeType === "Image"
                                    ? overlayBadge.largeImageBadgePath
                                    : overlayBadge.displayText
                            }
                            badgeTextColor={overlayBadge.textColorHexCode}
                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                            textBadgeCss={styles.badgeText}
                            imageBadgeProps={styles.badgeImageProps}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={styles.fullPrimaryImage}
                    // Valtech Custom Code
                    showLoader={true}
                />
                {/* Valtech Removed Carousel */}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
